import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, BarChart3, Target, Brain, Rocket } from 'lucide-react';
import RM2025View from './RM2025View';
import MenteExaminadorView from './MenteExaminadorView';
import './AnalisisMente.css';

const TABS = [
  { id: 'rm2025', label: 'RM 2025', sub: 'Baseline', Icon: BarChart3, enabled: true },
  { id: 'mente', label: 'Mente', sub: 'Examinador', Icon: Brain, enabled: true },
  { id: 'simulacros', label: 'Simulacros', sub: 'Verificación', Icon: Target, enabled: false },
  { id: 'viaje', label: 'Mi Viaje', sub: 'Progreso', Icon: Rocket, enabled: false },
];

export default function AnalisisDashboard({ darkMode = false, onBack }) {
  const [activeTab, setActiveTab] = useState('rm2025');

  const bg = darkMode ? 'bg-[#0a0e1a]' : 'bg-[#fafaf9]';
  const text = darkMode ? 'text-slate-100' : 'text-slate-900';
  const subText = darkMode ? 'text-slate-400' : 'text-slate-500';
  const muted = darkMode ? 'text-slate-500' : 'text-slate-400';
  const cardClass = darkMode ? 'am-card-dark' : 'am-card-light';

  return (
    <div className={`min-h-screen ${bg} ${text} am-sans relative overflow-hidden`}>
      {/* Atmospheric background gradients */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className={`absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl ${
            darkMode ? 'bg-indigo-500/10' : 'bg-indigo-200/40'
          }`}
        />
        <div
          className={`absolute top-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl ${
            darkMode ? 'bg-fuchsia-500/8' : 'bg-fuchsia-200/30'
          }`}
        />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-6 py-10">

        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
          className="flex items-start gap-5 mb-12"
        >
          <button
            onClick={onBack}
            className={`am-focus am-lift mt-2 flex items-center justify-center w-10 h-10 rounded-full ${
              darkMode ? 'bg-slate-800/60 hover:bg-slate-700/80 text-slate-300' : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
            }`}
            aria-label="Volver"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <div className="flex-1 min-w-0">
            <div className={`am-eyebrow mb-2 ${muted}`}>
              MEDIEXAMPRO · ANÁLISIS · BETA
            </div>
            <h1 className={`am-display text-5xl md:text-6xl font-medium ${text} mb-2`}>
              Mente del <span className="italic font-light">examinador</span>
            </h1>
            <p className={`am-sans text-sm md:text-base font-light ${subText} max-w-2xl`}>
              Análisis integrado de tus datos y la psicología detrás de cada pregunta del CONAREME.
            </p>
          </div>
        </motion.div>

        <motion.nav
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
          className={`mb-10 ${cardClass} am-noise rounded-2xl p-2 inline-flex flex-wrap gap-1`}
        >
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const Icon = tab.Icon;
            return (
              <button
                key={tab.id}
                onClick={() => tab.enabled && setActiveTab(tab.id)}
                disabled={!tab.enabled}
                className={`am-focus relative flex items-center gap-3 px-5 py-2.5 rounded-xl transition-all ${
                  isActive
                    ? darkMode
                      ? 'bg-slate-100 text-slate-900'
                      : 'bg-slate-900 text-white'
                    : tab.enabled
                      ? darkMode
                        ? 'text-slate-300 hover:bg-slate-800/60'
                        : 'text-slate-700 hover:bg-slate-100'
                      : darkMode
                        ? 'text-slate-600 cursor-not-allowed opacity-50'
                        : 'text-slate-400 cursor-not-allowed opacity-50'
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                <div className="text-left">
                  <div className="am-sans text-sm font-semibold leading-tight">{tab.label}</div>
                  <div className={`am-eyebrow text-[9px] mt-0.5 ${
                    isActive
                      ? darkMode ? 'text-slate-600' : 'text-slate-400'
                      : muted
                  }`}>
                    {tab.enabled ? tab.sub : 'PRÓXIMO'}
                  </div>
                </div>
              </button>
            );
          })}
        </motion.nav>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {activeTab === 'rm2025' && <RM2025View darkMode={darkMode} />}
            {activeTab === 'mente' && <MenteExaminadorView darkMode={darkMode} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
